import { useCallback, useEffect, useRef, useState } from "react";

import { ApplicationEndpoints } from "@generated/application";
import { useApi } from "@platform/hooks/useApi";
import { useSse, type SseMessage } from "@platform/hooks/useSse";

import { Application, type ApplicationListResponse } from "../types";

interface UseStacksByProjectReturn {
  systems: Application[];
  total: number;
  limit: number;
  offset: number;
  hasNext: boolean;
  hasPrevious: boolean;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setLimit: (limit: number) => void;
  nextPage: () => void;
  previousPage: () => void;
}

/**
 * Fetches the list of systems (applications) for a given project alias.
 * Also subscribes to SSE `application_created` events to auto-refresh
 * when a new stack is created.
 */
export function useStacksByProject(
  projectAlias: string | undefined,
): UseStacksByProjectReturn {
  const api = useApi();
  const [systems, setSystems] = useState<Application[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<ApplicationListResponse>(
        `${ApplicationEndpoints.LIST_APPLICATIONS}?projectAlias=${encodeURIComponent(projectAlias)}&limit=${limit}&offset=${offset}`,
        {
          errorMessage: `Failed to load systems for project: ${projectAlias}`,
          showLoading: false,
        },
      );
      if (!result) {
        throw new Error("Application list response was empty");
      }
      setSystems(result.applications);
      setTotal(result.total);
      setLimit(result.limit);
      setOffset(result.offset);
      setHasNext(result.hasNext);
      setHasPrevious(result.hasPrevious);
    } catch {
      setError("Failed to load stacks.");
    } finally {
      setLoading(false);
    }
  }, [projectAlias, limit, offset]);

  // Keep latest load fn in a ref so the SSE callback doesn't change identity
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  // Initial load
  useEffect(() => {
    void load();
  }, [load]);

  // SSE subscription for real-time stack creation
  const ssePath = projectAlias ? `sse/project/${projectAlias}` : null;

  const handleMessage = useCallback((msg: SseMessage) => {
    if (msg.type === "application_created") {
      void loadRef.current();
    }
  }, []);

  useSse(ssePath, handleMessage);

  const changeLimit = useCallback((nextLimit: number) => {
    if (![10, 20, 50, 100].includes(nextLimit)) return;
    setLimit(nextLimit);
    setOffset(0);
  }, []);

  const nextPage = useCallback(() => {
    if (hasNext) setOffset((current) => current + limit);
  }, [hasNext, limit]);

  const previousPage = useCallback(() => {
    if (hasPrevious) setOffset((current) => Math.max(0, current - limit));
  }, [hasPrevious, limit]);

  return {
    systems,
    total,
    limit,
    offset,
    hasNext,
    hasPrevious,
    loading,
    error,
    reload: load,
    setLimit: changeLimit,
    nextPage,
    previousPage,
  };
}
